import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Stock } from '../model/stock';
import { StockService } from '../../services/stock.service';
import { Store, StoreResponse } from '../../interfaces/store.interface';
import { StockLot } from '../../interfaces/stock.interface';
import { getStoresByInstitutionId } from '../../services/store.service';
import { AuthService } from '../../services/auth.service';
import NotInChargeNotice from '../store/NotInChargeNotice';
import {
  Link,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Box,
  TablePagination,
  IconButton,
  Collapse,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchableSelect from '../common/SearchableSelect';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { useToast } from '../../context/ToastContext';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { costingMethodLabel } from '../../utils/costingMethod';   // G6-0

// Category and store are secondary on a phone; item, quantity and unit stay visible.
const HIDE_ON_MOBILE = { display: { xs: 'none', sm: 'table-cell' } } as const;

interface StockRowProps {
  stock: Stock;
  rowNumber: number;
  canEditReorder: boolean;
  onEditReorder: (stock: Stock) => void;
}

const StockRow: React.FC<StockRowProps> = ({ stock, rowNumber, canEditReorder, onEditReorder }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [lots, setLots] = useState<StockLot[]>([]);
  const [lotsLoading, setLotsLoading] = useState<boolean>(false);
  const [lotsLoaded, setLotsLoaded] = useState<boolean>(false);

  // The lots endpoint (GET stock/lots/{store}/{item}) needs STOCK_MANAGE, the same key as editing the reorder level.
  const canShowLots = stock.item?.lotTracked === true && canEditReorder;

  const handleToggle = async () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && !lotsLoaded && stock.item?.id) {
      setLotsLoading(true);
      try {
        const data = await StockService.getLots(stock.storeId, stock.item.id);
        setLots(data);
        setLotsLoaded(true);
      } catch (error) {
        console.error('Error fetching lots:', error);
      } finally {
        setLotsLoading(false);
      }
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>
          {canShowLots && (
            <IconButton data-help="store.stock.action.expandLots" size="small" onClick={handleToggle} aria-label="expand row">
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{rowNumber}</TableCell>
        <TableCell>
          {stock.item.itemName}
          {/* G6-0: effective costing method */}
          <Typography component="div" variant="caption" color="text.secondary" data-testid="stock-costing">
            {costingMethodLabel(stock.item)}
          </Typography>
        </TableCell>
        <TableCell sx={HIDE_ON_MOBILE}>{stock.item?.itemCategory?.fullName || stock.item?.itemCategory?.itemCategoryName}</TableCell>
        <TableCell sx={HIDE_ON_MOBILE}>{stock.store.storeName}</TableCell>
        <TableCell>{stock.availableQty}</TableCell>
        <TableCell>{stock.item?.unit?.symbol ?? '-'}</TableCell>
        <TableCell sx={HIDE_ON_MOBILE}>{stock.totalValue != null ? formatMoney(stock.totalValue) : '-'}</TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {stock.reorderLevel ?? '-'}
          {canEditReorder && stock.id && (
            <Tooltip title="Edit reorder level" arrow>
              <IconButton data-help="store.stock.action.editReorder" size="small" aria-label="Edit reorder level" onClick={() => onEditReorder(stock)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
      </TableRow>
      {canShowLots && (
        <TableRow>
          <TableCell colSpan={9} sx={{ py: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ m: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mb: 0 }}>
                    Lots
                  </Typography>
                  {stock.item?.id && (
                    <Link
                      component={RouterLink}
                      to={`/fabric-lots?storeId=${stock.storeId}&itemId=${stock.item.id}`}
                      data-testid="fabric-lot-register-link"
                      data-help="store.stock.action.lotRegister"
                    >
                      View in Lot Register
                    </Link>
                  )}
                </Box>
                {lotsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Table size="small" data-help-section="store.stock.lots">
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell data-help="store.stock.lot.col.lotNo">Lot No</TableCell>
                        <TableCell data-help="store.stock.lot.col.receiptDate">Receipt Date</TableCell>
                        <TableCell data-help="store.stock.lot.col.unitCost">Unit Cost</TableCell>
                        <TableCell data-help="store.stock.lot.col.remaining">Remaining Qty</TableCell>
                        <TableCell data-help="store.stock.lot.col.received">Received Qty</TableCell>
                        <TableCell data-help="store.stock.lot.col.dyeLot">Dye Lot</TableCell>
                        <TableCell data-help="store.stock.lot.col.shade">Shade</TableCell>
                        <TableCell data-help="store.stock.lot.col.grade">Grade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lots.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            No lots found
                          </TableCell>
                        </TableRow>
                      ) : (
                        lots.map(lot => (
                          <TableRow key={lot.id}>
                            <TableCell>{lot.lotNo}</TableCell>
                            <TableCell>{lot.receiptDate ? formatDate(lot.receiptDate) : '—'}</TableCell>
                            <TableCell>{formatMoney(lot.unitCost)}</TableCell>
                            <TableCell>{lot.remainingQty}</TableCell>
                            <TableCell>{lot.receivedQty ?? '—'}</TableCell>
                            <TableCell>{lot.dyeLot ?? '—'}</TableCell>
                            <TableCell>{lot.shade ?? '—'}</TableCell>
                            <TableCell>{lot.grade ?? '—'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const StockList: React.FC = () => {
  const [stocks, setStocks] = useState<Stock[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [storesLoaded, setStoresLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const toast = useToast();
  const canEditReorder = usePermission(PERMISSIONS.STOCK_MANAGE);
  const [reorderTarget, setReorderTarget] = useState<Stock | null>(null);
  const [reorderValue, setReorderValue] = useState<string>('');
  const [savingReorder, setSavingReorder] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [pageInfo, setPageInfo] = useState({
    currentPage: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10
  });

  // Fetch stores on component mount
  useEffect(() => {
    const fetchStores = async () => {
      try {
        const response: StoreResponse = await getStoresByInstitutionId(0, 100, 'ALL'); // Fetch all stores with a large size
        setStores(response.content);
        setStoresLoaded(true);
      } catch (error) {
        toast.showApiError(error, 'Failed to load stores');
      }
    };

    fetchStores();
  }, []);

  // Debounce the search term
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  // Reset to first page whenever the debounced search changes
  useEffect(() => {
    setPageInfo(prev => ({ ...prev, currentPage: 0 }));
  }, [debouncedSearch]);

  // Fetch stocks when the store (or "All stores"), the page or the search changes
  useEffect(() => {
    fetchStocks(selectedStoreId, pageInfo.currentPage, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreId, pageInfo.currentPage, debouncedSearch]);

  // Only the newest request may fill the table, so a slow older page can't overwrite a newer filter's result.
  const requestSeq = useRef(0);

  const fetchStocks = async (storeId: string, page: number, search: string) => {
    // No store picked = the stock of every store the caller may see
    const institutionId = AuthService.getSelectedInstitution();
    if (!storeId && !institutionId) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = storeId
        ? await StockService.getStockByStore(storeId, page, pageInfo.size, search)
        : await StockService.getStockByInstitution(institutionId as string, page, pageInfo.size, search);
      if (seq !== requestSeq.current) return;
      setStocks(response.content);
      setPageInfo(prev => ({
        ...prev,
        currentPage: response.number,
        totalPages: response.totalPages,
        totalElements: response.totalElements,
        size: response.size
      }));
    } catch (error) {
      if (seq === requestSeq.current) console.error('Error fetching stocks:', error);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  const openReorderDialog = (stock: Stock) => {
    setReorderTarget(stock);
    setReorderValue(stock.reorderLevel != null ? String(stock.reorderLevel) : '');
  };

  const saveReorderLevel = async () => {
    if (!reorderTarget?.id) return;
    const trimmed = reorderValue.trim();
    const parsed = trimmed === '' ? null : Number(trimmed);
    if (parsed !== null && (!Number.isInteger(parsed) || parsed < 0)) {
      toast.showError('Enter a whole number, zero or more (or leave it blank to clear the level).');
      return;
    }
    try {
      setSavingReorder(true);
      const updated = await StockService.setReorderLevel(reorderTarget.id, parsed);
      setStocks(prev => prev.map(s => (s.id === reorderTarget.id ? { ...s, reorderLevel: updated?.reorderLevel ?? undefined } : s)));
      toast.showSuccess(parsed === null ? 'Reorder level cleared' : 'Reorder level saved');
      setReorderTarget(null);
    } catch (error) {
      toast.showApiError(error, 'Failed to save reorder level');
    } finally {
      setSavingReorder(false);
    }
  };

  const handleStoreChange = (storeId: string) => {
    setSelectedStoreId(storeId);
    setPageInfo(prev => ({...prev, currentPage: 0})); // Reset to first page when store changes
  };

  const handlePageChange = (_event: unknown, page: number) => {
    setPageInfo(prev => ({...prev, currentPage: page}));
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="store.stock.page">
      <Typography variant="h5" sx={{ mb: 2 }}>
        Stock List
      </Typography>

      {storesLoaded && stores.length === 0 && <NotInChargeNotice />}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }} data-help-section="store.stock.filter">
        <SearchableSelect<Store>
          options={stores}
          value={selectedStoreId}
          onChange={handleStoreChange}
          getOptionLabel={store => store.storeName}
          data-help="store.stock.filter.store" label="Select Store"
          disabled={stores.length === 0}
          noOptionsText="No stores available"
          placeholder="All stores"
          size="small"
          sx={{ flex: '1 1 260px', maxWidth: 300 }}
        />

        <TextField
          size="small"
          sx={{ flex: '1 1 260px', maxWidth: 300 }}
          data-help="store.stock.filter.search" label="Search by item name or code"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} data-help-section="store.stock.table">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5', '& th': { fontWeight: 'bold' } }}>
                  <TableCell />
                  <TableCell data-help="store.stock.col.no">#</TableCell>
                  <TableCell data-help="store.stock.col.item">Item</TableCell>
                  <TableCell data-help="store.stock.col.category" sx={HIDE_ON_MOBILE}>Category</TableCell>
                  <TableCell data-help="store.stock.col.store" sx={HIDE_ON_MOBILE}>Store</TableCell>
                  <TableCell data-help="store.stock.col.qty">Available Quantity</TableCell>
                  <TableCell data-help="store.stock.col.unit">Unit</TableCell>
                  <TableCell data-help="store.stock.col.value" sx={HIDE_ON_MOBILE}>Value</TableCell>
                  <TableCell data-help="store.stock.col.reorder">Reorder Level</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stocks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No stock items found
                    </TableCell>
                  </TableRow>
                ) : (
                  stocks.map((stock, index) => (
                    <StockRow
                      key={stock.id}
                      stock={stock}
                      rowNumber={index + 1 + pageInfo.currentPage * pageInfo.size}
                      canEditReorder={canEditReorder}
                      onEditReorder={openReorderDialog}
                    />
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={pageInfo.totalElements}
              page={pageInfo.currentPage}
              onPageChange={handlePageChange}
              rowsPerPage={pageInfo.size}
              rowsPerPageOptions={[pageInfo.size]}
            />
          </TableContainer>

        </>
      )}
      <Dialog data-help-page="store.reorder.page" open={reorderTarget !== null} onClose={() => !savingReorder && setReorderTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reorder level</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {reorderTarget?.item?.itemName}: the item shows on the Low Stock list when its quantity falls to this level.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            data-help="store.reorder.field.level" label="Reorder level"
            value={reorderValue}
            onChange={e => setReorderValue(e.target.value)}
            inputProps={{ min: 0, step: 1 }}
            helperText="Leave blank for no reorder level."
          />
        </DialogContent>
        <DialogActions>
          <Button data-help="store.reorder.cancel" onClick={() => setReorderTarget(null)} disabled={savingReorder}>Cancel</Button>
          <Button data-help="store.reorder.save" variant="contained" onClick={saveReorderLevel} disabled={savingReorder}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockList;
